import { Wallet } from '@solana/wallet-adapter-react'

import { CHAIN_CONFIGS } from '@/constants/chains'
import { Chain } from '@/types/wallet'
import { isEvmChain, isMobileDevice } from '@/utils'
import {
  SUPPORTED_NETWORK,
  WALLET_BINANCE,
  WALLET_BITGET,
  WALLET_COINBASE,
  WALLET_CONNECT,
  WALLET_ICONS,
  WALLET_IM_TOKEN,
  WALLET_LEDGER,
  WALLET_METAMASK,
  WALLET_OKX,
  WALLET_PHANTOM,
  WALLET_RABBY,
  WALLET_TREZOR,
  WALLET_TRUST,
  WALLET_UNISAT,
  WALLET_XVERSE,
  WalletType,
} from '@/utils/constants'
import {
  BinanceWallet,
  BitgetWallet,
  IWallet,
  LedgerWallet,
  OkxWallet,
  SolanaWallet,
  TrezorWallet,
  UnisatWallet,
  XverseWallet,
} from '@/wallets'
import { ImTokenWallet } from '@/wallets/ImTokenWallet'
import { PhantomWallet } from '@/wallets/PhantomWallet'
import { WagmiWallet } from '@/wallets/WagmiWallet'

import { Connector } from 'wagmi'

export const wagmiWallet = new WagmiWallet()
export const solanaWallet = new SolanaWallet()

// these wallet don't use kit
const okx = new OkxWallet()
const trezor = new TrezorWallet()
const bitget = new BitgetWallet()
const unisat = new UnisatWallet()
const xverse = new XverseWallet()
const ledger = new LedgerWallet()
const phantom = new PhantomWallet()
const binance = new BinanceWallet()
const imToken = new ImTokenWallet()

const EVM_WALLET_CONFIG = {
  [WALLET_TREZOR]: trezor,
  [WALLET_LEDGER]: ledger,
}

const SOL_WALLET_CONFIG = {}

const BTC_TESTNET_WALLET_CONFIG = {
  [WALLET_OKX]: okx,
  [WALLET_LEDGER]: ledger,
  [WALLET_TREZOR]: trezor,
  [WALLET_BITGET]: bitget,
  [WALLET_UNISAT]: unisat,
  [WALLET_XVERSE]: xverse,
  [WALLET_IM_TOKEN]: imToken,
}

const BTC_WALLET_CONFIG = {
  ...BTC_TESTNET_WALLET_CONFIG,
  [WALLET_BINANCE]: binance,
  [WALLET_PHANTOM]: phantom,
}
const LIST_WALLET_EVM = Object.keys(EVM_WALLET_CONFIG)
const LIST_WALLET_SOLANA = Object.keys(SOL_WALLET_CONFIG)
const LIST_WALLET_BTC = Object.keys(BTC_WALLET_CONFIG)
const LIST_WALLET_BTC_TESTNET = Object.keys(BTC_TESTNET_WALLET_CONFIG)

export const TAP_ROOT_DERIVATION_PATH = `m/86'/0'/0'/0/0`
export const NATIVE_SEGWIT_DERIVATION_PATH = `m/84'/0'/0'/0/0`

const CHAIN_ACTION_WALLET_TREZOR_EVM_CONFIG = [
  {
    name: "MetaMask (m/44'/60'/0'/0/0)",
    nameOption: "MetaMask (m/44'/60'/0'/0/{index})",
    value: "m/44'/60'/0'/0/0",
  },
  {
    name: "Ledger Live (m/44'/60'/0'/0/0)",
    nameOption: "Ledger Live (m/44'/60'/{index}'/0/0)",
    value: "m/44'/60'/0'/0/0",
  },
  {
    name: "Legacy (m/44'/60'/0'/0)",
    nameOption: "Legacy (m/44'/60'/0'/{index})",
    value: "m/44'/60'/0'/0",
  },
  {
    name: 'Custom Derivation',
    nameOption: 'Custom Derivation',
    isCustom: true,
    value: '',
  },
]

const LIST_ACTION = [
  {
    name: "Native Segwit (m/84'/0'/0'/0/{index})",
    nameOption: "Native Segwit (m/84'/0'/0'/0/{index})",
    value: "m/84'/0'/0'/0/{index}",
  },
  {
    name: "Native Segwit (m/84'/0'/{index}'/0/0)",
    nameOption: "Native Segwit (m/84'/0'/{index}'/0/0)",
    value: "m/84'/0'/{index}'/0/0",
  },
  {
    name: "Nested Segwit (m/49'/0'/0'/0/0))",
    nameOption: "Nested Segwit (m/49'/0'/0'/0/{index})",
    value: "m/49'/0'/0'/0/{index}",
  },
  {
    name: "Legacy (m/44'/0'/0'/0/0)",
    nameOption: "Legacy (m/44'/0'/0'/0/{index})",
    value: "m/44'/0'/0'/0/{index}",
  },
  {
    name: 'Custom Derivation',
    nameOption: 'Custom Derivation',
    isCustom: true,
    value: '',
  },
]

const LIST_ACTION_TESTNET = [
  {
    name: "Native Segwit (m/84'/1'/0'/0/{index})",
    nameOption: "Native Segwit (m/84'/1'/0'/0/{index})",
    value: "m/84'/1'/0'/0/{index}",
  },
  {
    name: "Native Segwit (m/84'/1'/{index}'/0/0)",
    nameOption: "Native Segwit (m/84'/1'/{index}'/0/0)",
    value: "m/84'/1'/{index}'/0/0",
  },
  {
    name: "Nested Segwit (m/49'/1'/0'/0/0))",
    nameOption: "Nested Segwit (m/49'/1'/0'/0/{index})",
    value: "m/49'/1'/0'/0/{index}",
  },
  {
    name: "Legacy (m/44'/1'/0'/0/0)",
    nameOption: "Legacy (m/44'/1'/0'/0/{index})",
    value: "m/44'/1'/0'/0/{index}",
  },
  {
    name: 'Custom Derivation',
    nameOption: 'Custom Derivation',
    isCustom: true,
    value: '',
  },
]

export const walletsConfig: Record<string, Record<string, IWallet>> = {
  [SUPPORTED_NETWORK.BTC]: BTC_WALLET_CONFIG,
  [SUPPORTED_NETWORK.BTC_TESTNET]: BTC_WALLET_CONFIG,
  [SUPPORTED_NETWORK.SOLANA]: SOL_WALLET_CONFIG,
  [SUPPORTED_NETWORK.SOLANA_DEVNET]: SOL_WALLET_CONFIG,
}

Object.keys(CHAIN_CONFIGS).forEach((network) => {
  if (isEvmChain(network)) {
    walletsConfig[network] = EVM_WALLET_CONFIG
  }
})

export const chains: Chain[] = [
  {
    network: SUPPORTED_NETWORK.BTC,
    wallets: LIST_WALLET_BTC,
    actions: {
      [WALLET_TREZOR]: LIST_ACTION,
      [WALLET_LEDGER]: LIST_ACTION,
    },
  },
  {
    network: SUPPORTED_NETWORK.BTC_TESTNET,
    wallets: LIST_WALLET_BTC_TESTNET,
    actions: {
      [WALLET_TREZOR]: LIST_ACTION_TESTNET,
      [WALLET_LEDGER]: LIST_ACTION_TESTNET,
    },
  },
  {
    network: SUPPORTED_NETWORK.SOLANA,
    wallets: LIST_WALLET_SOLANA,
    actions: {},
    networkId: '1',
  },
  {
    network: SUPPORTED_NETWORK.SOLANA_DEVNET,
    wallets: LIST_WALLET_SOLANA,
    actions: {},
    networkId: '1',
  },
]

Object.keys(CHAIN_CONFIGS).forEach((network) => {
  if (isEvmChain(network)) {
    chains.push({
      network,
      wallets: LIST_WALLET_EVM,
      actions: {
        [WALLET_TREZOR]: CHAIN_ACTION_WALLET_TREZOR_EVM_CONFIG,
        [WALLET_LEDGER]: CHAIN_ACTION_WALLET_TREZOR_EVM_CONFIG,
      },
      networkId: CHAIN_CONFIGS[network].chainId.toString(),
    })
  }
})

export type WalletInfo = {
  name: string
  type: string
  icon: string
  key: string
  wallet?: IWallet
  adapter?: Connector | Wallet
  isDetected: () => boolean
  priorityEvm?: number // 0 is the highest
  priorityBtc?: number // 0 is the highest
  priority: number // check priorityEvm and priorityBtc first, if not exist, check this priority: 0 is the highest, -1 is not supported
}

export const listWallets: WalletInfo[] = [
  {
    name: 'OKX Wallet',
    type: WalletType.WALLET_TYPE_BROWSER_WALLET,
    key: WALLET_OKX,
    isDetected: okx.isDetected,
    priority: okx.priority(),
    priorityEvm: 2,
    priorityBtc: 0,
  },
  {
    name: 'Binance Wallet',
    type: WalletType.WALLET_TYPE_BROWSER_WALLET,
    key: WALLET_BINANCE,
    isDetected: binance.isDetected,
    priority: binance.priority(),
    priorityWagmi: 2,
  },
  {
    name: 'Trust Wallet',
    type: WalletType.WALLET_TYPE_BROWSER_WALLET,
    key: WALLET_TRUST,
    isDetected: () => false,
    priority: 0,
    priorityEvm: 3,
  },
  {
    name: 'Rabby Wallet',
    type: WalletType.WALLET_TYPE_BROWSER_WALLET,
    key: WALLET_RABBY,
    isDetected: () => false,
    priority: 0,
    priorityEvm: 1,
  },
  {
    name: 'Xverse Wallet',
    type: WalletType.WALLET_TYPE_BROWSER_WALLET,
    key: WALLET_XVERSE,
    isDetected: xverse.isDetected,
    priority: xverse.priority(),
    priorityEvm: 6,
    priorityBtc: 3,
  },
  {
    name: 'Meta Mask',
    type: WalletType.WALLET_TYPE_BROWSER_WALLET,
    key: WALLET_METAMASK,
    isDetected: () => false,
    priority: 0,
    priorityEvm: 0,
  },
  {
    name: 'Phantom',
    type: WalletType.WALLET_TYPE_BROWSER_WALLET,
    key: WALLET_PHANTOM,
    isDetected: phantom.isDetected,
    priority: 4,
    priorityEvm: 5,
  },
  {
    name: 'Bitget Wallet',
    type: WalletType.WALLET_TYPE_BROWSER_WALLET,
    key: WALLET_BITGET,
    isDetected: bitget.isDetected,
    priority: bitget.priority(),
    priorityEvm: 4,
    priorityBtc: 2,
  },
  {
    name: 'Unisat Wallet',
    type: WalletType.WALLET_TYPE_BROWSER_WALLET,
    key: WALLET_UNISAT,
    isDetected: unisat.isDetected,
    priority: unisat.priority(),
    priorityEvm: 5,
    priorityBtc: 1,
  },
  {
    name: 'Wallet connect',
    type: WalletType.WALLET_TYPE_BROWSER_WALLET,
    key: WALLET_CONNECT,
    isDetected: () => false,
    priority: 0,
    priorityEvm: isMobileDevice() ? 2 : 5,
  },
  {
    name: 'Coinbase',
    type: WalletType.WALLET_TYPE_BROWSER_WALLET,
    key: WALLET_COINBASE,
    isDetected: () => false,
    priority: 0,
    priorityEvm: 3,
  },
  {
    name: 'Ledger',
    type: WalletType.WALLET_TYPE_HARDWARE_WALLET,
    key: WALLET_LEDGER,
    isDetected: () => false,
    priority: ledger.priority(),
    priorityEvm: 10,
    priorityBtc: 4,
  },
  {
    name: 'Trezor',
    type: WalletType.WALLET_TYPE_HARDWARE_WALLET,
    key: WALLET_TREZOR,
    isDetected: () => false,
    priority: trezor.priority(),
    priorityEvm: 10,
    priorityBtc: 5,
  },
  {
    name: 'ImToken',
    type: WalletType.WALLET_TYPE_BROWSER_WALLET,
    key: WALLET_IM_TOKEN,
    isDetected: imToken.isDetected,
    priority: imToken.priority(),
    priorityBtc: 6,
  },
].map((e) => ({
  ...e,
  icon: WALLET_ICONS[e.key],
}))
